(() => {
  'use strict';
  async function presentPreview(view, buffer, description, current) {
    const blob = await new Promise((resolve, reject) => buffer.toBlob(value => value ? resolve(value) : reject(new Error('Preview unavailable')), 'image/png'));
    const url = URL.createObjectURL(blob), image = new Image();
    image.dataset.colorPreview = '';
    image.alt = description;
    image.width = buffer.width;
    image.height = buffer.height;
    image.src = url;
    try {
      await image.decode();
      if (!current() || !view.isConnected) { URL.revokeObjectURL(url); return false; }
      const previous = view.querySelector('[data-color-preview]');
      if (previous) { previous.replaceWith(image); URL.revokeObjectURL(previous.src); }
      else view.querySelector('canvas').after(image);
      view.querySelector('canvas').hidden = true;
      view.querySelector('[data-comfort-fallback]').hidden = true;
      return true;
    } catch (error) { URL.revokeObjectURL(url); throw error; }
  }
  function restorePreview(view) {
    const image = view.querySelector('[data-color-preview]');
    if (image) { URL.revokeObjectURL(image.src); image.remove(); }
    view.querySelector('canvas').hidden = true;
    view.querySelector('[data-comfort-fallback]').hidden = false;
  }

  const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));
  const rgb = hex => hex.match(/[a-f\d]{2}/gi).slice(0, 3).map(v => parseInt(v, 16));
  const luminance = (r, g, b) => r * .2126 + g * .7152 + b * .0722;
  function colorGate(r, g, b, gate) {
    if (gate === 'none') return 1;
    if (gate === 'surface') return 1;
    const max = Math.max(r, g, b), min = Math.min(r, g, b), sat = (max - min) / Math.max(1, max);
    if (gate === 'blue') return clamp((b - r - 2) / 13) * clamp((b - g + 2) / 10);
    if (gate === 'green') return clamp((g - r) / 3) * clamp((g - b + 2) / 3);
    if (gate === 'red') return clamp((r - g - 2) / 12) * clamp((r - b + 2) / 10);
    if (gate === 'yellow') return clamp((Math.min(r, g) - b - 5) / 15);
    // Keep coloured logos/reflectors, pure background and very dark hardware untouched.
    return clamp((.22 - sat) / .08) * clamp((max - 12) / 18);
  }
  async function makeRenderer(imageUrl, layers, maxWidth = 1440) {
    const image = new Image(); image.crossOrigin = 'anonymous';
    const source = new URL(imageUrl, location.href);
    if (source.hostname === 'cdn.shopify.com') source.searchParams.set('width', String(maxWidth));
    image.src = source.href;
    await image.decode();
    const width = Math.min(maxWidth, image.naturalWidth), height = Math.round(width * image.naturalHeight / image.naturalWidth);
    const canvas = document.createElement('canvas'); canvas.width = width; canvas.height = height;
    const ctx = canvas.getContext('2d', {willReadFrequently: true});ctx.drawImage(image, 0, 0, width, height);
    const base = ctx.getImageData(0, 0, width, height);
    ctx.filter = "blur(1.2px)";ctx.drawImage(image,0,0,width,height);ctx.filter="none";const smooth = ctx.getImageData(0,0,width,height).data;
    ctx.filter = "blur(" + (width/1000*2.0) + "px)";ctx.drawImage(image,0,0,width,height);ctx.filter="none";const clean = ctx.getImageData(0,0,width,height).data;
    const masks = layers.map(layer => {
      const mask = document.createElement('canvas');mask.width = width;mask.height = height;
      const m = mask.getContext('2d', {willReadFrequently: true});m.scale(width/1000, width/1000);
      m.fillStyle = '#fff';m.fill(new Path2D(layer.path));
      const core=layer.cleanSurface?m.getImageData(0,0,width,height).data:null;
      if(layer.cleanSurface){m.strokeStyle='#fff';m.lineJoin='round';m.lineWidth=2*(layer.edgeExpansion||0);m.stroke(new Path2D(layer.path));}
      let exclusions;
      if(layer.exclude && layer.cleanSurface && (['fenders','rear'].includes(layer.group)||layer.hueAwareExclusions) && layer.sourceHue!=='neutral'){
        exclusions=document.createElement('canvas');exclusions.width=width;exclusions.height=height;
        const e=exclusions.getContext('2d');e.scale(width/1000,width/1000);e.fill(new Path2D(layer.exclude));const exclusionPixels=e.getImageData(0,0,width,height).data;exclusions.width=exclusions.height=0;exclusions=exclusionPixels;
      }else if (layer.exclude) {m.globalCompositeOperation = 'destination-out';m.fill(new Path2D(layer.exclude));}
      
      const pixels = m.getImageData(0, 0, width, height).data, weights = new Uint8Array(width*height);
      let protection;
      if(layer.protectDark){m.clearRect(0,0,1000,height*1000/width);m.globalCompositeOperation='source-over';m.fill(new Path2D(layer.protectDark));protection=m.getImageData(0,0,width,height).data;}
      const light = [];
      for(let i = 0; i < weights.length; i++) {
        const p = i*4; let weight = pixels[p+3]/255 * colorGate(smooth[p],smooth[p+1],smooth[p+2],layer.gate);
        if(layer.cleanSurface){weight*=core[p+3]/255+(1-core[p+3]/255)*(layer.edgeHue ? colorGate(smooth[p],smooth[p+1],smooth[p+2],layer.sourceHue) : clamp((252-Math.min(base.data[p],base.data[p+1],base.data[p+2]))/7));if(exclusions?.[p+3])weight*=1-exclusions[p+3]/255*(1-colorGate(smooth[p],smooth[p+1],smooth[p+2],layer.sourceHue));}
        if(protection?.[p+3]) weight*=clamp((luminance(base.data[p],base.data[p+1],base.data[p+2])-55)/55);
        if(layer.includeHueBounds){
          const x=(i%width)*1000/width,y=Math.floor(i/width)*1000/width,b=layer.includeHueBounds;
          const hi=Math.max(smooth[p],smooth[p+1],smooth[p+2]),lo=Math.min(smooth[p],smooth[p+1],smooth[p+2]);
          if(x>=b[0]&&y>=b[1]&&x<=b[2]&&y<=b[3]&&hi>30&&(hi-lo)/hi>.23&&colorGate(smooth[p],smooth[p+1],smooth[p+2],layer.sourceHue)>.8)weight=1;
        }
        weights[i] = Math.round(weight*255);
        if (weight>.8 && i%13===0) light.push(luminance(base.data[p],base.data[p+1],base.data[p+2]));
      }
      mask.width=mask.height=0;
      light.sort((a,b)=>a-b);
      return {...layer, weights, core, light: Math.max(layer.fineDetail?10:30, layer.light ?? light[Math.floor(light.length*.58)] ?? 128)};
    });
    // Expanded edge coverage must never cover the interior of an adjoining panel.
    // Keep each panel's original core coverage; only trim its added edge pixels.
    for (const layer of masks) {
      if (!layer.core) continue;
      for(let i=0;i<layer.weights.length;i++) {
        const own=layer.core[i*4+3]/255;
        if(own>=1 || !layer.weights[i]) continue;
        let occupied=0;
        for(const other of masks) if(other!==layer && other.core) occupied=Math.max(occupied,other.core[i*4+3]/255*other.weights[i]/255);
        layer.weights[i]=Math.round(layer.weights[i]*(own+(1-own)*(1-occupied)));
      }
    }
    return {canvas, render(choices) {
      const result = new ImageData(new Uint8ClampedArray(base.data), width, height);
      const precise=layers.some(l=>l.cleanSurface), totals=precise?new Float32Array(base.data.length):null;
      for(const layer of masks) {
        const choice = choices[layer.group];if(layer.neutralize){for(let i=0;i<layer.weights.length;i++){const p=i*4;let weight=layer.weights[i]/255;if(!weight)continue;let occupied=0;for(const other of masks)if(!other.neutralize&&choices[other.group]?.hex&&!choices[other.group]?.keepOriginal)occupied+=other.weights[i]/255;weight*=1-clamp(occupied);const v=luminance(base.data[p],base.data[p+1],base.data[p+2])*(layer.dark?.12:1);for(let c=0;c<3;c++)totals[p+c]+=(v-base.data[p+c])*weight;totals[p+3]+=weight;}continue;}if(!choice?.hex || choice.keepOriginal) continue;
        const target = rgb(choice.hex), targetLight = luminance(...target);
        for(let i=0;i<layer.weights.length;i++) {
          const weight = layer.weights[i]/255;if(!weight) continue;
          const p=i*4, rawLight=luminance(base.data[p],base.data[p+1],base.data[p+2]);
          const oldLight=layer.cleanSurface ? (layer.detail??.08)*rawLight+(1-(layer.detail??.08))*(layer.fineDetail?luminance(smooth[p],smooth[p+1],smooth[p+2]):luminance(clean[p],clean[p+1],clean[p+2])) : rawLight;
          // Preserve the original photo's shading and metallic texture on either side of its midtone.
          const shade = oldLight/layer.light;
          const baseShadow = layer.cleanSurface ? (layer.darkFloor??(targetLight > 180 ? .80 : targetLight > 100 ? .60 : .30)) : 0;
          const value = layer.cleanSurface ? (shade <= 1 ? targetLight * (baseShadow + (1-baseShadow)*Math.pow(shade,.8)) : targetLight+(255-targetLight)*clamp((oldLight-layer.light)/Math.max(110,255-layer.light))*.82) : shade <= 1 ? targetLight * (layer.light < 70 ? .65 + .35 * shade : Math.pow(shade,.75)) : targetLight + (255-targetLight)*clamp((oldLight-layer.light)/(255-layer.light))*.72;
          for(let c=0;c<3;c++) {
            const colored = value <= targetLight ? target[c] * value/Math.max(1,targetLight) : target[c]+(255-target[c])*(value-targetLight)/Math.max(1,255-targetLight);
            if(precise)totals[p+c]+=(colored-base.data[p+c])*weight;
            else result.data[p+c] = base.data[p+c]*(1-weight)+colored*weight;
          }
          if(precise)totals[p+3]+=weight;
        }
      }
      if(precise)for(let p=0;p<base.data.length;p+=4){const w=Math.max(1,totals[p+3]);for(let c=0;c<3;c++)result.data[p+c]=base.data[p+c]+totals[p+c]/w;}
      ctx.putImageData(result,0,0);return canvas;
    }};
  }

  function initPortal(view, root, config) {
    const canvas = view.querySelector('canvas');
    const fallback = view.querySelector('[data-comfort-fallback]');
    const status = view.querySelector('[data-comfort-status]');
    const section = root.closest('.shopify-section');



    const buy = [...(section?.querySelectorAll('product-form,.shopify-payment-button,sticky-atc-panel') || [])].map(el=>({el,hidden:el.hidden}));
    let renderer, revision = 0;
    const selects = () => [...root.querySelectorAll('.bock-color-group input:checked')];
    const group = label => /tak/.test(label) ? 'roof' : /front/.test(label) ? 'front' : /stänk|skärm/.test(label) ? 'fenders' : /sidostöd/.test(label) ? 'rear' : /nederdel/.test(label) ? 'body' : /ramp/.test(label) ? 'ramp' : null;
    function quote() {
      const link = root.querySelector('[data-proposal-quote]'); if(!link)return;
      const chosen=selects();chosen.forEach(input=>{const output=input.closest('.bock-color-group')?.querySelector('[data-current-color]');if(output)output.textContent=input.value;});
      link.href='mailto:info@equistore.se?subject='+encodeURIComponent('Färgförslag – '+root.dataset.title)+'&body='+encodeURIComponent('Hej! Jag vill ha en offert på '+root.dataset.title+' med följande färgförslag:\n'+chosen.map(i=>i.dataset.label+': '+i.value).join('\n')+'\n\nProduktsida: '+location.href);
    }
    async function render() {
      const version=++revision;view.setAttribute('aria-busy','true');status.textContent='Uppdaterar färgvisningen…';
      try {
        if(!renderer)renderer=makeRenderer(config.portal.source,config.portal.layers);
        const engine=await renderer;if(version!==revision||!view.isConnected)return;
        const selected=selects(),choices={},unseen=[];
        for(const input of selected){
          const key=group((input.dataset.label||'').toLowerCase());if(!key)continue;
          const chip=input.closest('label')?.querySelector('.bock-chip');
          let hex=input.dataset.color || (chip && getComputedStyle(chip).getPropertyValue('--bock-color').trim());
          if(!/^#[0-9a-f]{6}$/i.test(hex||''))hex=/svart|omål|olack|ofärg/i.test(input.value)?'#202020':'#A5A5A4';
          // Non-painted fittings retain their original finish.
          choices[key]={hex};
          if(!config.portal.layers.some(l=>l.group===key))unseen.push(input.dataset.label.replace('Färg på ',''));
        }
        engine.render(choices);
        const description=(root.dataset.title||config.portal.title)+' – '+selected.map(i=>i.dataset.label+': '+i.value).join(', ');
        if (!await presentPreview(view,engine.canvas,description,()=>version===revision)) return;view.dataset.rendered=selected.map(i=>i.dataset.key).join(',');
        status.textContent=selected.map(i=>i.dataset.label.replace('Färg på ','')+': '+i.value).join(' · ')+(unseen.length?' · '+unseen.join(', ')+' visas inte i denna vy.':'');
      }catch(error){
        if(version!==revision)return;renderer=null;restorePreview(view);delete view.dataset.rendered;
        status.textContent='Färgvisningen kunde inte laddas. Dina färgval är sparade. Välj färgen igen för att försöka på nytt.';

      }finally{if(version===revision)view.setAttribute('aria-busy','false');}
    }
    root.addEventListener('change',event=>{if(!event.target.matches('.bock-color-group input'))return;if(config.proposal)buy.forEach(x=>x.el.hidden=true);quote();render();});
    root.addEventListener('click',event=>{if(!view.dataset.rendered && event.target.matches('.bock-color-group input:checked'))render();});
    root.querySelector('[data-proposal-reset]')?.addEventListener('click',()=>{
      root.querySelectorAll('.bock-color-group input').forEach(input=>input.checked=input.defaultChecked);
      
      buy.forEach(x=>x.el.hidden=x.hidden);quote();render();
    });
    quote();render();
  }

  function smoothPanelSeam(pixels, points) {
    if (!points) return;
    const source = new Uint8ClampedArray(pixels);
    const heightAt = x => {
      for (let n = 1; n < points.length; n++) {
        if (x <= points[n][0]) {
          const [x0, y0] = points[n - 1];
          const [x1, y1] = points[n];
          return y0 + (y1 - y0) * (x - x0) / (x1 - x0);
        }
      }
      return points[points.length - 1][1];
    };
    const left = points[0][0], right = points[points.length - 1][0];
    for (let x = Math.ceil(left); x <= right; x++) {
      const center = heightAt(x);
      for (let y = Math.floor(center - 4); y <= Math.ceil(center + 4); y++) {
        const strength = Math.min(1, Math.max(0, 4 - Math.abs(y - center)), (x - left) / 4, (right - x) / 4);
        if (strength <= 0) continue;
        const p = (y * 1024 + x) * 4;
        for (let channel = 0; channel < 3; channel++) {
          let total = 0, weight = 0;
          for (let dx = -3; dx <= 3; dx++) {
            const sx = Math.max(left, Math.min(right, x + dx));
            const sy = y + heightAt(sx) - center;
            for (const [dy, verticalWeight] of [[-0.5, 0.2], [0, 0.6], [0.5, 0.2]]) {
              const iy = Math.floor(sy + dy), blend = sy + dy - iy;
              const sample = (iy * 1024 + sx) * 4 + channel;
              const w = (4 - Math.abs(dx)) * verticalWeight;
              total += (source[sample] * (1 - blend) + source[sample + 4096] * blend) * w;
              weight += w;
            }
          }
          pixels[p + channel] = source[p + channel] * (1 - strength) + total / weight * strength;
        }
      }
    }
  }
  function init(scope = document) {
    scope.querySelectorAll('[data-remaining-colors]').forEach(view => {
      if (view.dataset.ready) return;
      view.dataset.ready = 'true';
      const root = view.closest('[data-remaining-config]');
      const config = JSON.parse(view.querySelector('[data-comfort-color-data]').textContent);
      if(config.portal){initPortal(view,root,config);return;}
      const canvas = view.querySelector('canvas');
      const context = canvas.getContext('2d');
      const fallback = view.querySelector('[data-comfort-fallback]');
      const status = view.querySelector('[data-comfort-status]');
      const cache = new Map();
      let revision = 0;
      let basePromise;
      let pendingRender;
      const combinations = new Map();
      async function pixels(url, signal) {
        const response = await fetch(url, {headers: {Accept: 'image/png'}, signal});
        if (!response.ok) throw new Error('Image unavailable');
        const objectUrl = URL.createObjectURL(await response.blob());
        return new Promise((resolve, reject) => {
          const image = new Image();
          image.crossOrigin = 'anonymous';
          image.onload = () => {
            try {
              if (!image.naturalWidth || !image.naturalHeight) throw new Error('Unexpected dimensions');
              const buffer = document.createElement('canvas');
              buffer.width = 1024;
              buffer.height = 722;
              const ctx = buffer.getContext('2d', {willReadFrequently: true});
              const scale = Math.min(1024 / image.naturalWidth, 722 / image.naturalHeight);
              const width = image.naturalWidth * scale, height = image.naturalHeight * scale;
              ctx.drawImage(image, (1024 - width) / 2, (722 - height) / 2, width, height);
              resolve(ctx.getImageData(0, 0, 1024, 722).data);
            } catch (error) { reject(error); }
            finally { URL.revokeObjectURL(objectUrl); }
          };
          image.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Image unavailable')); };
          image.src = objectUrl;
        });
      }
      function load(url) {
        if (cache.has(url)) {
          const result = cache.get(url);
          cache.delete(url);
          cache.set(url, result);
          return result;
        }
        const result = pixels(url).catch(error => { cache.delete(url); throw error; });
        cache.set(url, result);
        if (cache.size > 10) cache.delete(cache.keys().next().value);
        return result;
      }
      async function manufacturerPixels(selected, signal) {
        const keys = selected.map(input => input.dataset.key).filter(key => /^\d+$/.test(key) && !config.defaults.includes(key));
        const signature = keys.join(',');
        if (combinations.has(signature)) return combinations.get(signature);
        const api = 'https://boeckmann-configurator-backend-live.azurewebsites.net/api/en/SE/configurator/';
        const response = await fetch(api + 'model-detail/' + config.rendererModel, {signal});
        if (!response.ok) throw new Error('Model unavailable');
        const model = await response.json();
        if (!model.configurationId) throw new Error('Configuration unavailable');
        for (const key of keys) {
          const update = await fetch(api + 'accessory-click/' + encodeURIComponent(model.configurationId) + '/' + key, {
            method: 'PUT', headers: {'Content-Type': 'application/json'}, body: '1', signal
          });
          if (!update.ok) throw new Error('Color unavailable');
          const state = await update.json();
          if (state.mandatoryChangesCategories?.length) throw new Error('Color requires additional choices');
          if (!state.updatedOptions?.some(option => String(option.accessoryId) === key && option.checked)) throw new Error('Color not applied');
        }
        const result = await pixels(api + 'configuration/' + encodeURIComponent(model.configurationId) + '/image?transparent=true', signal);
        combinations.set(signature, result);
        if (combinations.size > 8) combinations.delete(combinations.keys().next().value);
        return result;
      }
      function selections() {
        return Array.from(root.querySelectorAll('.bock-color-group input:checked'));
      }
      async function render() {
        const version = ++revision;
        const selected = selections();
        const urls = selected.map(input => config.colors?.[input.dataset.key]).filter(Boolean);
        pendingRender?.abort();
        pendingRender = new AbortController();
        const renderController = pendingRender;
        const timeout = setTimeout(() => renderController.abort(), 25000);
        view.setAttribute('aria-busy', 'true');
        status.textContent = 'Uppdaterar färgvisningen…';
        try {
          let result;
          if (config.roof) { result = await maskedPixels(selected); if (version !== revision) return; } else if (config.rendererModel) {
            result = new Uint8ClampedArray(await manufacturerPixels(selected, pendingRender.signal));
            if (version !== revision || !view.isConnected) return;
          } else {
          if (!basePromise) basePromise = pixels(config.base).catch(error => { basePromise = null; throw error; });
          const [base, variants] = await Promise.all([basePromise, Promise.all(urls.map(load))]);
          if (version !== revision || !view.isConnected) return;
          result = new Uint8ClampedArray(base);
          for (const variant of variants) {
            for (let p = 0; p < result.length; p += 4) {
              if (variant[p] !== base[p] || variant[p + 1] !== base[p + 1] || variant[p + 2] !== base[p + 2] || variant[p + 3] !== base[p + 3]) {
                result[p] = variant[p];
                result[p + 1] = variant[p + 1];
                result[p + 2] = variant[p + 2];
                result[p + 3] = variant[p + 3];
              }
            }
          }
          }
          smoothPanelSeam(result, config.seam);
          context.putImageData(new ImageData(result, 1024, 722), 0, 0);
          const description = root.dataset.title + ' – ' + selected.map(input => input.dataset.label + ': ' + input.value).join(', ');
          if (!await presentPreview(view,canvas,description,()=>version===revision)) return;
          view.dataset.rendered = selected.map(input => input.dataset.key).join(',');
          status.textContent = selected.map(input => input.dataset.label.replace('Färg på ', '') + ': ' + input.value).join(' · ');

        } catch (error) {
          if (version !== revision) return;
          status.textContent = 'Färgvisningen kunde inte laddas. Dina färgval är sparade. Välj färgen igen för att försöka på nytt.';
          restorePreview(view);
          delete view.dataset.rendered;

        } finally {
          clearTimeout(timeout);
          if (version === revision) view.setAttribute('aria-busy', 'false');
        }
      }
      root.addEventListener('change', event => {
        if (event.target.matches('.bock-color-group input')) { view.dataset.interacted = 'true'; render(); }
      });
      root.addEventListener('click', event => {
        if (!view.dataset.rendered && event.target.matches('.bock-color-group input:checked')) render();
      });
      const section = root.closest('.shopify-section');


      const buy = [...(section?.querySelectorAll('product-form,.shopify-payment-button,sticky-atc-panel') || [])].map(el=>({el,hidden:el.hidden}));
      function quote() { const chosen=selections();chosen.forEach(input=>{const output=input.closest('.bock-color-group')?.querySelector('[data-current-color]');if(output)output.textContent=input.value;});root.querySelector('[data-proposal-quote]').href='mailto:info@equistore.se?subject='+encodeURIComponent('Färgförslag – '+root.dataset.title)+'&body='+encodeURIComponent('Hej! Jag vill ha en offert på '+root.dataset.title+' med följande färgförslag:\n'+chosen.map(i=>i.dataset.label+': '+i.value).join('\n')+'\n\nProduktsida: '+location.href); }
      root.addEventListener('change',()=>{buy.forEach(x=>x.el.hidden=true);quote();});
      root.querySelector('[data-proposal-reset]').addEventListener('click',()=>{delete view.dataset.interacted;buy.forEach(x=>x.el.hidden=x.hidden);root.querySelectorAll('input[type=radio]').forEach(el=>el.checked=el.defaultChecked);quote();render();});
      quote();render();
    });
  }
  init();
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', event => init(event.target));
})();


